// @ts-strict-ignore
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';

import { type KeyMap } from 'helpers/interface';
import { mockCustomerDetails } from 'services/socket-lc3/customer/event-handling/mockCustomerDetails';
import { type EntityData } from 'store/entities/interfaces';

import {
  CustomerCustomVariableName,
  type IAddOrUpdateCustomersPayload,
  type IAddVisitedPagePayload,
  type ICustomer,
  type ICustomerDetails,
  type ICustomersState,
  type IRemoveCustomerPayload,
  type IRemoveCustomersPayload,
  type ISetCustomersDataPayload,
  type IUpdateCustomerPayload,
  type IVisitedPage,
} from '../interfaces';

import { REMOVED_CUSTOMERS_LIMIT } from './constants';

const sortVisitedPageIds = (visitedPages: KeyMap<IVisitedPage>): string[] =>
  Object.keys(visitedPages).sort((a, b) => Number(a) - Number(b));

export function getStateForUpsertCustomerDetails(state: ICustomersState, details: ICustomerDetails): ICustomersState {
  const { customer, visitedPages = null } = details;

  const sourceCustomer = customer?.customVariables?.[CustomerCustomVariableName.SampleCustomer]
    ? { ...mockCustomerDetails.customer, id: customer.id }
    : customer;

  const { id } = sourceCustomer;

  const isNew = !state.byIds[id];
  const allIds = isNew ? [...state.allIds, id] : state.allIds;

  const hasVisitedPagesUpdate = !!visitedPages;
  const visitedPagesUpdated = hasVisitedPagesUpdate
    ? {
        byIds: visitedPages,
        allIds: sortVisitedPageIds(visitedPages),
      }
    : null;

  // we don't want to merge customVariables, it should be always replaced
  const oldCustomer = state.byIds[id] ? { ...state.byIds[id] } : undefined;
  delete oldCustomer?.customVariables;

  // we don't want to merge geolocation, it should be replaced if explicitly provided
  if ('geolocation' in sourceCustomer) {
    delete oldCustomer?.geolocation;
  }

  const newCustomer = merge({}, oldCustomer, sourceCustomer);

  return {
    ...state,
    byIds: {
      ...state.byIds,
      [id]: {
        ...newCustomer,
        ...(newCustomer.groupIds && { groupIds: newCustomer.groupIds.map(String) }),
      },
    },
    allIds,
    ...(state.removedByIds?.[id] && {
      removedByIds: (({ [id]: _, ...rest }) => rest)(state.removedByIds),
    }),
    visitedPages: hasVisitedPagesUpdate
      ? { ...state.visitedPages, [id]: visitedPagesUpdated }
      : state.visitedPages || {},
  };
}

function keepOnlyLatestCustomers(
  removedByIdsMap: KeyMap<ICustomer>,
  activeByIds: KeyMap<ICustomer>,
  visitedPagesMap: KeyMap<EntityData<IVisitedPage>>,
  threshold: number,
): { removedByIds: KeyMap<ICustomer>; visitedPages: KeyMap<EntityData<IVisitedPage>> } {
  const removedByIds = { ...removedByIdsMap };
  const visitedPages = { ...visitedPagesMap };
  const removedByIdsKeys = Object.keys(removedByIds);
  if (removedByIdsKeys.length > threshold) {
    removedByIdsKeys.slice(0, removedByIdsKeys.length - threshold).forEach((key) => {
      delete removedByIds[key];
      // Defense-in-depth: returning customers are cleaned from removedByIds on
      // arrival (upsert/addOrUpdate), but guard against any edge case where a
      // stale entry survives so we don't wipe an active customer's visitedPages.
      if (!activeByIds[key]) {
        delete visitedPages[key];
      }
    });
  }

  return { removedByIds, visitedPages };
}

export function getStateForRemoveCustomer(state: ICustomersState, payload: IRemoveCustomerPayload): ICustomersState {
  const { customerId } = payload;

  const { [customerId]: removedCustomer, ...newByIds } = state.byIds;
  const newAllIds = state.allIds.filter((id) => id != customerId);

  const { removedByIds, visitedPages } = keepOnlyLatestCustomers(
    { ...state.removedByIds, [customerId]: removedCustomer },
    newByIds,
    state.visitedPages,
    REMOVED_CUSTOMERS_LIMIT,
  );

  return {
    ...state,
    byIds: newByIds,
    allIds: newAllIds,
    removedByIds,
    visitedPages,
  };
}

export function getStateForRemoveCustomers(state: ICustomersState, payload: IRemoveCustomersPayload): ICustomersState {
  const { customerIds } = payload;

  const result = state.allIds.reduce(
    (acc, customerId) => {
      const customer = state.byIds[customerId];

      if (customerIds.includes(customerId)) {
        acc.removed[customerId] = customer;
      } else {
        acc.preserved[customerId] = customer;
      }

      return acc;
    },
    { preserved: {}, removed: {} },
  );

  const newByIds = result.preserved;
  const newAllIds = Object.keys(newByIds);
  const removedCustomers = result.removed;
  const { removedByIds, visitedPages } = keepOnlyLatestCustomers(
    {
      ...state.removedByIds,
      ...removedCustomers,
    },
    newByIds,
    state.visitedPages,
    REMOVED_CUSTOMERS_LIMIT,
  );

  return {
    ...state,
    byIds: newByIds,
    allIds: newAllIds,
    removedByIds,
    visitedPages,
  };
}

export function getStateForSetData(state: ICustomersState, payload: ISetCustomersDataPayload): ICustomersState {
  const { customers } = payload;

  return {
    ...state,
    byIds: customers,
    allIds: Object.keys(customers),
    removedByIds: {},
  };
}

export function getStateForAddOrUpdateCustomers(
  state: ICustomersState,
  payload: IAddOrUpdateCustomersPayload,
): ICustomersState {
  const { customers, visitedPages = {} } = payload;

  const byIdsUpdated = { ...state.byIds };
  const visitedPagesUpdates = {};
  const newCustomerIds = [];

  Object.keys(customers).forEach((customerId) => {
    const customer = customers[customerId];
    const existingCustomer = state.byIds[customerId];

    if (existingCustomer) {
      const oldCustomer = { ...existingCustomer };

      // we don't want to merge geolocation, it should be replaced if explicitly provided
      if ('geolocation' in customer) {
        delete oldCustomer.geolocation;
      }

      byIdsUpdated[customerId] = merge({}, oldCustomer, customer);
    } else {
      byIdsUpdated[customerId] = customer;
      newCustomerIds.push(customerId);
    }

    if (byIdsUpdated[customerId].groupIds) {
      byIdsUpdated[customerId].groupIds = byIdsUpdated[customerId].groupIds.map(String);
    }

    const hasVisitedPagesUpdate = !!visitedPages[customerId];
    if (hasVisitedPagesUpdate) {
      visitedPagesUpdates[customerId] = {
        byIds: visitedPages[customerId],
        allIds: sortVisitedPageIds(visitedPages[customerId]),
      };
    }
  });

  const hasAddedNewCustomers = newCustomerIds.length > 0;
  const hasUpdatedVisitedPages = !isEmpty(visitedPagesUpdates);

  // Clean stale removedByIds entries for customers who are coming back,
  // so they don't inflate the map and trigger visitedPages cleanup later.
  const hasReturningCustomers = state.removedByIds && Object.keys(customers).some((id) => state.removedByIds[id]);

  const removedByIds = hasReturningCustomers
    ? Object.keys(customers).reduce<KeyMap<ICustomer>>(
        (acc, id) => {
          delete acc[id];

          return acc;
        },
        { ...state.removedByIds },
      )
    : state.removedByIds;

  const allIdsUpdated = hasAddedNewCustomers ? [...state.allIds, ...newCustomerIds] : state.allIds;
  const visitedPagesUpdated = hasUpdatedVisitedPages
    ? { ...state.visitedPages, ...visitedPagesUpdates }
    : state.visitedPages || {};

  return {
    ...state,
    byIds: byIdsUpdated,
    allIds: allIdsUpdated,
    removedByIds,
    visitedPages: visitedPagesUpdated,
  };
}

export function getStateForUpdateCustomer(state: ICustomersState, payload: IUpdateCustomerPayload): ICustomersState {
  return getStateForUpsertCustomerDetails(state, payload);
}

export function getStateForAddVisitedPage(state: ICustomersState, payload: IAddVisitedPagePayload): ICustomersState {
  const { customerId, visitedPage } = payload;
  const customerVisitedPages = state.visitedPages[customerId];
  const updatedByIds = {
    ...(customerVisitedPages?.byIds || {}),
    [visitedPage.timestampInMS]: {
      ...visitedPage,
    },
  };

  return {
    ...state,
    visitedPages: {
      ...(state.visitedPages || {}),
      [customerId]: {
        byIds: updatedByIds,
        allIds: sortVisitedPageIds(updatedByIds),
      },
    },
  };
}
